import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  UserGroupIcon,
  AcademicCapIcon,
  UsersIcon,
  UserIcon,
  ChevronDownIcon,
  XMarkIcon,
  CheckIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline'
import { useQuery } from '@tanstack/react-query'
import { getGrades, getStreams, type Grade, type Stream } from '@/api/academicApi'
import type { TargetAudience, AudienceType } from '@/api/communicationApi'

interface AudienceSelectorProps {
  value: TargetAudience
  onChange: (audience: TargetAudience) => void
  showPreview?: boolean
  previewData?: {
    totalRecipients: number
    breakdown: {
      students: number
      parents: number
      teachers: number
      staff: number
    }
  }
  disabled?: boolean
}

const audienceTypes: { type: AudienceType; label: string; icon: typeof UserGroupIcon; description: string }[] = [
  { type: 'all_school', label: 'Entire School', icon: AcademicCapIcon, description: 'All students, parents, teachers, and staff' },
  { type: 'grades', label: 'By Grade', icon: UserGroupIcon, description: 'All students, parents & teachers in selected grades' },
  { type: 'streams', label: 'By Stream', icon: UsersIcon, description: 'All students, parents & teachers in selected streams' },
  { type: 'grade_parents', label: 'Parents by Grade', icon: UserIcon, description: 'Only parents of students in selected grades' },
  { type: 'stream_parents', label: 'Parents by Stream', icon: UserIcon, description: 'Only parents of students in selected streams' },
  { type: 'parents_only', label: 'All Parents', icon: UserIcon, description: 'All parents in the school' },
  { type: 'staff_only', label: 'Staff Only', icon: UserIcon, description: 'Teachers and admin staff' },
  { type: 'individual_users', label: 'Individual Users', icon: UserIcon, description: 'Select specific users' }
]

export function AudienceSelector({
  value,
  onChange,
  showPreview = true,
  previewData,
  disabled = false
}: AudienceSelectorProps) {
  const [isTypeOpen, setIsTypeOpen] = useState(false)
  const [isGradeOpen, setIsGradeOpen] = useState(false)
  const [isStreamOpen, setIsStreamOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  // Fetch grades
  const { data: gradesData, isLoading: gradesLoading } = useQuery({
    queryKey: ['grades-for-audience'],
    queryFn: () => getGrades({ limit: 100, status: 'active' }),
    staleTime: 5 * 60 * 1000
  })

  // Fetch streams
  const { data: streamsData, isLoading: streamsLoading } = useQuery({
    queryKey: ['streams-for-audience'],
    queryFn: () => getStreams(),
    staleTime: 5 * 60 * 1000
  })

  const grades = gradesData?.grades || []
  const streams = streamsData || []

  const selectedType = audienceTypes.find(t => t.type === value.type) || audienceTypes[0]

  const handleTypeChange = (type: AudienceType) => {
    let roles: string[] | undefined
    if (type === 'parents_only' || type === 'grade_parents' || type === 'stream_parents') {
      roles = ['PARENT']
    } else if (type === 'staff_only') {
      roles = ['TEACHER', 'SCHOOL_ADMIN']
    }

    onChange({
      type,
      gradeIds: [],
      streamIds: [],
      userIds: [],
      roles
    })
    setIsTypeOpen(false)
  }

  const handleGradeToggle = (gradeId: string) => {
    const currentGrades = value.gradeIds || []
    const newGrades = currentGrades.includes(gradeId)
      ? currentGrades.filter(id => id !== gradeId)
      : [...currentGrades, gradeId]

    onChange({
      ...value,
      gradeIds: newGrades
    })
  }

  const handleStreamToggle = (streamId: string) => {
    const currentStreams = value.streamIds || []
    const newStreams = currentStreams.includes(streamId)
      ? currentStreams.filter(id => id !== streamId)
      : [...currentStreams, streamId]

    onChange({
      ...value,
      streamIds: newStreams
    })
  }

  const handleSelectAllGrades = () => {
    const allGradeIds = grades.map(g => g._id)
    const allSelected = allGradeIds.every(id => value.gradeIds?.includes(id))

    onChange({
      ...value,
      gradeIds: allSelected ? [] : allGradeIds
    })
  }

  const handleSelectAllStreams = () => {
    const allStreamIds = streams.map(s => s._id)
    const allSelected = allStreamIds.every(id => value.streamIds?.includes(id))

    onChange({
      ...value,
      streamIds: allSelected ? [] : allStreamIds
    })
  }

  const filteredGrades = grades.filter(grade =>
    grade.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const filteredStreams = streams.filter(stream =>
    stream.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    stream.gradeName?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const getSelectedCount = () => {
    if (value.type === 'grades' || value.type === 'grade_parents') return value.gradeIds?.length || 0
    if (value.type === 'streams' || value.type === 'stream_parents') return value.streamIds?.length || 0
    if (value.type === 'individual_users') return value.userIds?.length || 0
    return 0
  }

  return (
    <div className="space-y-4">
      {/* Audience Type Selector */}
      <div className="relative">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Target Audience
        </label>
        <button
          type="button"
          disabled={disabled}
          onClick={() => setIsTypeOpen(!isTypeOpen)}
          className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-300 rounded-lg shadow-sm hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <selectedType.icon className="h-5 w-5 text-primary-600" />
            </div>
            <div className="text-left">
              <p className="text-sm font-medium text-gray-900">{selectedType.label}</p>
              <p className="text-xs text-gray-500">{selectedType.description}</p>
            </div>
          </div>
          <ChevronDownIcon className={`h-5 w-5 text-gray-400 transition-transform ${isTypeOpen ? 'rotate-180' : ''}`} />
        </button>

        <AnimatePresence>
          {isTypeOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
            >
              {audienceTypes.map((type) => {
                const IconComponent = type.icon
                const isSelected = value.type === type.type

                return (
                  <button
                    key={type.type}
                    type="button"
                    onClick={() => handleTypeChange(type.type)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 transition-colors ${
                      isSelected ? 'bg-primary-50' : ''
                    }`}
                  >
                    <div className={`p-2 rounded-lg ${isSelected ? 'bg-primary-100' : 'bg-gray-100'}`}>
                      <IconComponent className={`h-5 w-5 ${isSelected ? 'text-primary-600' : 'text-gray-600'}`} />
                    </div>
                    <div className="flex-1 text-left">
                      <p className={`text-sm font-medium ${isSelected ? 'text-primary-900' : 'text-gray-900'}`}>
                        {type.label}
                      </p>
                      <p className="text-xs text-gray-500">{type.description}</p>
                    </div>
                    {isSelected && (
                      <CheckIcon className="h-5 w-5 text-primary-600" />
                    )}
                  </button>
                )
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Grade Selection */}
      {(value.type === 'grades' || value.type === 'grade_parents') && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-700">
              Select Grades
            </label>
            <button
              type="button"
              onClick={handleSelectAllGrades}
              className="text-xs text-primary-600 hover:text-primary-700 font-medium"
            >
              {value.gradeIds?.length === grades.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>

          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search grades..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Grade List */}
          <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
            {gradesLoading ? (
              <div className="px-4 py-6 text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto" />
              </div>
            ) : filteredGrades.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-gray-500">
                No grades found
              </div>
            ) : (
              filteredGrades.map((grade) => {
                const isSelected = value.gradeIds?.includes(grade._id)
                return (
                  <button
                    key={grade._id}
                    type="button"
                    onClick={() => handleGradeToggle(grade._id)}
                    className={`w-full flex items-center justify-between px-4 py-2 hover:bg-gray-50 transition-colors ${
                      isSelected ? 'bg-primary-50' : ''
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                        isSelected ? 'bg-primary-600 border-primary-600' : 'border-gray-300'
                      }`}>
                        {isSelected && <CheckIcon className="h-3 w-3 text-white" />}
                      </div>
                      <span className="text-sm text-gray-900">{grade.name}</span>
                    </div>
                    <span className="text-xs text-gray-500">
                      {grade.studentCount || 0} students
                    </span>
                  </button>
                )
              })
            )}
          </div>

          {/* Selected Tags */}
          {(value.gradeIds?.length || 0) > 0 && (
            <div className="flex flex-wrap gap-2">
              {value.gradeIds?.map(gradeId => {
                const grade = grades.find(g => g._id === gradeId)
                if (!grade) return null
                return (
                  <span
                    key={gradeId}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700"
                  >
                    {grade.name}
                    <button
                      type="button"
                      onClick={() => handleGradeToggle(gradeId)}
                      className="ml-1 hover:text-primary-900"
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  </span>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* Stream Selection */}
      {(value.type === 'streams' || value.type === 'stream_parents') && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-700">
              Select Streams
            </label>
            <button
              type="button"
              onClick={handleSelectAllStreams}
              className="text-xs text-primary-600 hover:text-primary-700 font-medium"
            >
              {value.streamIds?.length === streams.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>

          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search streams..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Stream List */}
          <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
            {streamsLoading ? (
              <div className="px-4 py-6 text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto" />
              </div>
            ) : filteredStreams.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-gray-500">
                No streams found
              </div>
            ) : (
              filteredStreams.map((stream) => {
                const isSelected = value.streamIds?.includes(stream._id)
                return (
                  <button
                    key={stream._id}
                    type="button"
                    onClick={() => handleStreamToggle(stream._id)}
                    className={`w-full flex items-center justify-between px-4 py-2 hover:bg-gray-50 transition-colors ${
                      isSelected ? 'bg-primary-50' : ''
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                        isSelected ? 'bg-primary-600 border-primary-600' : 'border-gray-300'
                      }`}>
                        {isSelected && <CheckIcon className="h-3 w-3 text-white" />}
                      </div>
                      <div className="text-left">
                        <span className="text-sm text-gray-900">{stream.name}</span>
                        {stream.gradeName && (
                          <span className="text-xs text-gray-500 ml-2">({stream.gradeName})</span>
                        )}
                      </div>
                    </div>
                    <span className="text-xs text-gray-500">
                      {stream.studentCount || 0} students
                    </span>
                  </button>
                )
              })
            )}
          </div>

          {/* Selected Tags */}
          {(value.streamIds?.length || 0) > 0 && (
            <div className="flex flex-wrap gap-2">
              {value.streamIds?.map(streamId => {
                const stream = streams.find(s => s._id === streamId)
                if (!stream) return null
                return (
                  <span
                    key={streamId}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700"
                  >
                    {stream.name}
                    <button
                      type="button"
                      onClick={() => handleStreamToggle(streamId)}
                      className="ml-1 hover:text-primary-900"
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  </span>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* Audience Preview */}
      {showPreview && previewData && (
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Audience Preview</h4>
          <div className="grid grid-cols-2 gap-3">
            <div className="text-center p-2 bg-white rounded-lg">
              <p className="text-2xl font-bold text-primary-600">{previewData.totalRecipients}</p>
              <p className="text-xs text-gray-500">Total Recipients</p>
            </div>
            <div className="space-y-1.5">
              {previewData.breakdown.students > 0 && (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600">Students</span>
                  <span className="font-medium">{previewData.breakdown.students}</span>
                </div>
              )}
              {previewData.breakdown.parents > 0 && (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600">Parents</span>
                  <span className="font-medium">{previewData.breakdown.parents}</span>
                </div>
              )}
              {previewData.breakdown.teachers > 0 && (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600">Teachers</span>
                  <span className="font-medium">{previewData.breakdown.teachers}</span>
                </div>
              )}
              {previewData.breakdown.staff > 0 && (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600">Staff</span>
                  <span className="font-medium">{previewData.breakdown.staff}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AudienceSelector
